/**
 * useChartTimeWindow (#2436) — the shared React hook that gives any of the three
 * app time-series charts stock-app-style zoom + pan over its (categorical, sorted)
 * rows array. Owns the ``IndexWindow`` view state and exposes:
 *   - ``visibleRows`` — the sliced rows to hand to recharts (windowed rendering);
 *   - ``zoomIn/zoomOut/panPast/panFuture/reset`` + ``can*`` flags (for the
 *     ``ChartZoomControls`` buttons);
 *   - ``containerProps`` — spread onto the plot wrapper to wire the PRIMARY
 *     desktop gestures: click-drag pan (the pointer is captured only once a real
 *     drag begins, so a plain click still reaches the child plot), plus full
 *     keyboard operability (``+``/``−`` zoom, ``←``/``→`` pan, ``Home``/``0``
 *     reset) on a focusable, labelled region (a11y: not mouse-only). Mouse-wheel
 *     is intentionally NOT wired to zoom (#2457) — it hijacked page scroll.
 *
 * All the math lives in the pure, unit-tested ``timeWindow.ts``; this hook is the
 * thin stateful/DOM shell around it.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import {
  clampWindow,
  focalIndexAt,
  isFit,
  MIN_VISIBLE_ROWS,
  panBy,
  sliceRows,
  visibleCount,
  zoomAround,
  type IndexWindow,
} from './timeWindow';

const ZOOM_IN_FACTOR = 0.6;
const ZOOM_OUT_FACTOR = 1 / 0.6;
/** A button/keyboard pan step covers this fraction of the visible window. */
const PAN_FRACTION = 0.3;
/** Below this pointer travel (px) a press is a click (select), not a drag (pan). */
const DRAG_THRESHOLD_PX = 3;

export interface ChartTimeWindowContainerProps {
  tabIndex: number;
  role: 'group';
  'aria-label': string;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
  style: CSSProperties;
}

export interface UseChartTimeWindowResult<T> {
  /** The current inclusive index window into ``rows``. */
  window: IndexWindow;
  /** ``rows`` sliced to the window — feed THIS to recharts. */
  visibleRows: T[];
  canZoomIn: boolean;
  canZoomOut: boolean;
  /** Can pan earlier (toward the earliest data point). */
  canPanPast: boolean;
  /** Can pan later (toward the forecast horizon). */
  canPanFuture: boolean;
  /** True when the window is the default (fit) window. */
  isDefault: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  panPast: () => void;
  panFuture: () => void;
  reset: () => void;
  /** Spread onto the element that wraps the chart plot. */
  containerProps: ChartTimeWindowContainerProps;
}

export interface UseChartTimeWindowOptions {
  /** Accessible name for the interactive plot region. */
  ariaLabel?: string;
  /**
   * The default (initial + reset) inclusive index window. A caller-supplied
   * sub-range opens the chart narrower than the full rows array — e.g. the
   * preisspiegel 1J view spans a year of rows but opens on today ±6mo, pannable
   * left into the past. Omit ⇒ the whole array (fit).
   */
  defaultWindow?: IndexWindow;
  /**
   * Additive click-to-select primitive (#2456): fired on pointer-up when the
   * press did NOT become a drag-pan (travel < ``DRAG_THRESHOLD_PX``), carrying
   * the index into ``visibleRows`` under the pointer. The click-vs-drag split is
   * the SAME one the pan uses, so a no-travel press selects while a genuine drag
   * pans — and pointer capture is deferred to a real drag (#2457) so the press
   * still reaches the child plot. The index is derived from the container's own
   * geometry, so it is only pixel-accurate for a plot that fills the container;
   * a chart with an inset axis (e.g. a recharts YAxis) should instead read the
   * clicked bucket from recharts' own ``onClick`` state. Callers that omit this
   * get the unchanged behaviour (the other two charts pass nothing).
   */
  onSelectIndex?: (index: number) => void;
  /**
   * Optional CONTROLLED mode (#2955 §1). Supply ``window`` and the hook renders
   * THAT instead of its own state, reporting every zoom/pan/reset through
   * ``onWindowChange`` for the caller to apply. Omit it and the hook is
   * byte-identical to before — self-updating, resetting on a data change — which
   * is what the other consumer (ernteprognose) still relies on.
   *
   * It exists because two charts that must share ONE window cannot each own
   * their own state, and the reset-on-signature-change effect below defeats
   * every way around that: swapping the rows array on a single instance changes
   * ``count`` (≈760 days vs 25 months), and syncing two instances through
   * ``defaultWindow`` changes the signature too — both force a reset on every
   * mode switch, which is precisely the bug a shared window exists to fix. In
   * controlled mode the internal state and that effect are both bypassed.
   *
   * ``onWindowChange`` is not fired for a clamp: the caller's window is rendered
   * bounds-safe, but silently rewriting the caller's state from a render would
   * make the two disagree about who owns it.
   */
  window?: IndexWindow;
  onWindowChange?: (window: IndexWindow) => void;
}

const panStepOf = (w: IndexWindow): number => Math.max(1, Math.round(visibleCount(w) * PAN_FRACTION));

export function useChartTimeWindow<T>(
  rows: readonly T[],
  options: UseChartTimeWindowOptions = {},
): UseChartTimeWindowResult<T> {
  const count = rows.length;

  // The default (initial + reset) window: a caller-supplied sub-range or the
  // whole array. Read as primitives so an unmemoised option object doesn't churn.
  const ds = options.defaultWindow ? options.defaultWindow.start : 0;
  const de = options.defaultWindow ? options.defaultWindow.end : count - 1;
  const defaultWin = useMemo(() => clampWindow({ start: ds, end: de }, count), [ds, de, count]);

  const [rawWindow, setRawWindow] = useState<IndexWindow>(() => defaultWin);

  // #2955 §1 — controlled mode. Read as primitives so an unmemoised caller
  // object does not churn the memo below.
  const isControlled = options.window !== undefined;
  const cs = options.window ? options.window.start : 0;
  const ce = options.window ? options.window.end : 0;

  // Reset to the default window when the data length OR the default range changes
  // (grain / period / item switch) — a stale window over new data is meaningless.
  // Skipped when controlled: there the caller owns the window, and resetting it
  // from here is exactly the force-reset-on-every-mode-switch bug. The signature
  // is still recorded so a later uncontrolled render does not see a stale one.
  const prevSig = useRef('');
  useEffect(() => {
    const sig = `${count}|${defaultWin.start}|${defaultWin.end}`;
    if (prevSig.current !== sig) {
      prevSig.current = sig;
      if (!isControlled) setRawWindow(defaultWin);
    }
  }, [count, defaultWin, isControlled]);

  // Always render/act against a bounds-safe window (guards the one render between
  // a data change and the reset effect, and any external staleness).
  const window = useMemo(
    () => clampWindow(isControlled ? { start: cs, end: ce } : rawWindow, count),
    [isControlled, cs, ce, rawWindow, count],
  );
  const visibleRows = useMemo(() => sliceRows(rows, window), [rows, window]);

  // Latest window/count for the imperative pointer-drag closure.
  const live = useRef({ window, count });
  live.current = { window, count };

  // Latest onSelectIndex callback for the imperative pointer closure (so the
  // ``[]``-dep pointerdown handler always fires the current render's callback).
  const selectCbRef = useRef(options.onSelectIndex);
  selectCbRef.current = options.onSelectIndex;

  // Latest controlled-mode wiring, for the same reason: the callbacks below are
  // stable across renders and must still see this render's props.
  const ctrl = useRef({ isControlled, onWindowChange: options.onWindowChange });
  ctrl.current = { isControlled, onWindowChange: options.onWindowChange };

  /**
   * The single write path for every gesture. Uncontrolled it is ``setState``;
   * controlled it resolves the update against the window currently being
   * RENDERED (the caller's) and hands the result over without storing it —
   * storing it too would double-apply each gesture once the caller applied it.
   */
  const setWindow = useCallback((next: IndexWindow | ((w: IndexWindow) => IndexWindow)) => {
    if (!ctrl.current.isControlled) {
      setRawWindow(next);
      return;
    }
    ctrl.current.onWindowChange?.(typeof next === 'function' ? next(live.current.window) : next);
  }, []);

  const zoomAtCentre = useCallback(
    (factor: number) =>
      setWindow((w) => {
        const safe = clampWindow(w, count);
        return zoomAround(safe, focalIndexAt(safe, 0.5), factor, count);
      }),
    [count, setWindow],
  );
  const zoomIn = useCallback(() => zoomAtCentre(ZOOM_IN_FACTOR), [zoomAtCentre]);
  const zoomOut = useCallback(() => zoomAtCentre(ZOOM_OUT_FACTOR), [zoomAtCentre]);
  const panPast = useCallback(
    () => setWindow((w) => { const s = clampWindow(w, count); return panBy(s, -panStepOf(s), count); }),
    [count, setWindow],
  );
  const panFuture = useCallback(
    () => setWindow((w) => { const s = clampWindow(w, count); return panBy(s, panStepOf(s), count); }),
    [count, setWindow],
  );
  const reset = useCallback(() => setWindow(defaultWin), [defaultWin, setWindow]);

  // Mouse-wheel over the chart is intentionally NOT wired to zoom (#2457): it
  // hijacked page scroll and read as disorienting. Zoom is via the +/− buttons,
  // the +/− keys, and touch pinch; the wheel scrolls the page as normal.

  // ── Keyboard: +/− zoom, ←/→ pan, Home/0 reset ──────────────────────────────
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
        case '_':
          zoomOut();
          break;
        case 'ArrowLeft':
          panPast();
          break;
        case 'ArrowRight':
          panFuture();
          break;
        case 'Home':
        case '0':
          reset();
          break;
        default:
          return;
      }
      e.preventDefault();
    },
    [zoomIn, zoomOut, panPast, panFuture, reset],
  );

  // ── Click-drag pan (plain drag, no modifier; suppresses text selection) ─────
  // The active drag's teardown, kept in a ref so a pointercancel (touch scroll
  // reclaim) OR an unmount mid-drag can always release the document listeners +
  // pointer capture — otherwise they leak and accumulate stale closures.
  const dragTeardown = useRef<(() => void) | null>(null);
  useEffect(() => () => dragTeardown.current?.(), []);

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const pointerId = e.pointerId;
    const startWindow = clampWindow(live.current.window, live.current.count);
    const vis = visibleCount(startWindow);
    const pxPerRow = rect.width > 0 ? rect.width / Math.max(1, vis - 1) : 1;
    let dragged = false;
    // Defensive: a prior drag that never saw its terminal event gets torn down first.
    dragTeardown.current?.();
    const onMove = (ev: PointerEventInit & { clientX?: number }) => {
      const dx = (ev.clientX ?? startX) - startX;
      if (!dragged && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (!dragged) {
        dragged = true;
        // Claim the pointer only once a real drag begins — NOT on pointerdown.
        // Capturing on the initial press retargets the subsequent mouseup/click
        // away from the child plot (recharts), swallowing its click-to-select a
        // day (#2457). Deferring it keeps a plain click passing through while a
        // genuine drag-pan still grabs the pointer.
        try {
          el.setPointerCapture?.(pointerId);
        } catch {
          /* jsdom / unsupported — capture is best-effort */
        }
      }
      // Drag content right (dx > 0) → reveal earlier data → window moves left.
      const delta = -Math.round(dx / pxPerRow);
      setWindow(panBy(startWindow, delta, live.current.count));
    };
    // Shared cleanup for BOTH pointerup and pointercancel: a touch vertical-scroll
    // that began on the chart hands the gesture back to the browser via
    // pointercancel, never pointerup, so a pointerup-only teardown leaks the
    // document listeners.
    const cleanup = () => {
      document.removeEventListener('pointermove', onMove as EventListener);
      document.removeEventListener('pointerup', onUp as EventListener);
      document.removeEventListener('pointercancel', onCancel);
      try {
        el.releasePointerCapture?.(pointerId);
      } catch {
        /* capture may already be released — best-effort */
      }
      dragTeardown.current = null;
    };
    // pointerup: a release that never crossed the drag threshold is a click →
    // fire the additive onSelectIndex primitive (#2456) with the pointed row.
    const onUp = (ev: PointerEventInit & { clientX?: number }) => {
      const wasDrag = dragged;
      const upX = ev.clientX ?? startX;
      cleanup();
      if (wasDrag) return;
      const cb = selectCbRef.current;
      if (!cb) return;
      const localX = upX - rect.left;
      const idx = Math.min(Math.max(0, vis - 1), Math.max(0, Math.round(localX / pxPerRow)));
      cb(idx);
    };
    // pointercancel: the gesture was reclaimed (e.g. touch scroll), NOT a click —
    // tear down only, never select.
    const onCancel = () => cleanup();
    dragTeardown.current = cleanup;
    document.addEventListener('pointermove', onMove as EventListener);
    document.addEventListener('pointerup', onUp as EventListener);
    document.addEventListener('pointercancel', onCancel);
    // ``setWindow`` is itself stable, so the handler identity is unchanged.
  }, [setWindow]);

  return {
    window,
    visibleRows,
    canZoomIn: visibleCount(window) > MIN_VISIBLE_ROWS,
    canZoomOut: !isFit(window, count),
    canPanPast: window.start > 0,
    canPanFuture: window.end < count - 1,
    isDefault: window.start === defaultWin.start && window.end === defaultWin.end,
    zoomIn,
    zoomOut,
    panPast,
    panFuture,
    reset,
    containerProps: {
      tabIndex: 0,
      role: 'group',
      'aria-label': options.ariaLabel ?? 'Diagramm (zoom- und verschiebbar)',
      onKeyDown,
      onPointerDown,
      // Keep the browser focus ring (a11y: the plot is a keyboard-operable
      // region); ``pan-y`` lets vertical touch-scroll through while we own the
      // horizontal gesture. ``userSelect: none`` (#2456 Ask 2) stops a drag-pan
      // (or any drag across the plot) from selecting the SVG axis / label text on
      // all three charts — the whole plot is a click-drag surface.
      style: { touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none' },
    },
  };
}
